import { isAppLocale, isKinPackId, kinPack, matchLocale, type AppLocale, type KinPack, type KinPackId } from '@lineage/core';
import { create } from 'zustand';

/**
 * Language preferences: the app language and the relationship words (PRD 11). Kept on the device so they
 * work signed out and offline, and copied from the account whenever /me loads, so a choice follows you to
 * every device. null means automatic. Needs no React providers, so the prerendered landing page can use it.
 */
const KIN = 'lineage.kinPack';
const LOCALE = 'lineage.locale';

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, v: string | null) => {
  try {
    if (v) localStorage.setItem(key, v);
    else localStorage.removeItem(key);
  } catch {
    /* private mode or prerender */
  }
};

interface Prefs {
  /** Chosen relationship words, or null to follow the app language. */
  kinPack: KinPackId | null;
  /** Chosen app language, or null to follow the device. */
  locale: AppLocale | null;
  setKinPack: (v: KinPackId | null) => void;
  setLocale: (v: AppLocale | null) => void;
}

export const usePrefs = create<Prefs>((set) => ({
  kinPack: ((v) => (isKinPackId(v) ? v : null))(read(KIN)),
  locale: ((v) => (isAppLocale(v) ? v : null))(read(LOCALE)),
  setKinPack: (v) => {
    write(KIN, v);
    set({ kinPack: v });
  },
  setLocale: (v) => {
    write(LOCALE, v);
    set({ locale: v });
  },
}));

/** The device's (or browser's) language, if the app speaks it; else English. */
export function deviceLocale(): AppLocale {
  if (typeof navigator === 'undefined') return 'en';
  return matchLocale(navigator.languages?.length ? navigator.languages : [navigator.language]);
}

/** App language: your choice, else the device's. */
export const resolveLocale = (choice: AppLocale | null): AppLocale => choice ?? deviceLocale();

/** Relationship words: your choice, else the word set of your app language. */
export const resolveKinPack = (choice: KinPackId | null, locale: AppLocale): KinPack => kinPack(choice ?? locale);

export function useLocale(): AppLocale {
  return resolveLocale(usePrefs((s) => s.locale));
}

export function useKinPack(): KinPack {
  const chosen = usePrefs((s) => s.kinPack);
  return resolveKinPack(chosen, useLocale());
}

/** Copies the account's choices onto this device (the account wins). Fields an older API omits are left alone. */
export function syncPrefs(user: { kinPack?: string | null; locale?: string | null }) {
  const s = usePrefs.getState();
  if (user.kinPack !== undefined) {
    const v = isKinPackId(user.kinPack) ? user.kinPack : null;
    if (v !== s.kinPack) s.setKinPack(v);
  }
  if (user.locale !== undefined) {
    const v = isAppLocale(user.locale) ? user.locale : null;
    if (v !== s.locale) s.setLocale(v);
  }
}
